<template>
  <aside class="cfg-edit-sidebar" @click.stop>
    <div class="cfg-edit-hdr">
      <h2>Editor</h2>
      <button type="button" class="cfg-edit-close" title="Close" @click="$emit('close')">✕</button>
    </div>

    <div class="cfg-edit-body">
      <div class="cfg-section cfg-section-dashboard">
        <h3 class="cfg-section-title">Dashboard</h3>
        <label class="cfg-lbl">Title</label>
        <input
          class="cfg-inp"
          v-model="titleDraft"
          @input="applyTitle"
          placeholder="Dashboard title"
        />
      </div>

      <div v-if="!selection" class="cfg-edit-empty">
        Click a <strong>filter</strong> label (in edit mode), a KPI card, or a chart. Or use <strong>+ Add filter</strong>, <strong>+ Add KPI</strong>, or <strong>+ Add chart</strong> below.
      </div>

      <!-- Filter editor -->
      <template v-else-if="selection.type === 'filter' && filter">
        <p class="cfg-edit-hint">Filter ({{ selection.index + 1 }})</p>

        <div class="cfg-section">
          <h3 class="cfg-section-title">General</h3>
          <label class="cfg-lbl">Label</label>
          <input class="cfg-inp" v-model="filterDraft.label" @input="applyFilter" placeholder="Shown in UI" />

          <label class="cfg-lbl">Column</label>
          <select class="cfg-inp" v-model="filterDraft.column" @change="applyFilter">
            <option value="" disabled>Select column…</option>
            <option v-for="col in columnOptions(filterDraft.column)" :key="'fcol-' + col" :value="col">{{ col }}</option>
          </select>

          <label class="cfg-lbl">Type</label>
          <select class="cfg-inp" v-model="filterDraft.type" @change="applyFilter">
            <option value="date_range">Date range</option>
            <option value="single_select">Single select</option>
            <option value="multi_select">Multi select</option>
          </select>
        </div>

        <div class="cfg-section cfg-section-danger">
          <button type="button" class="cfg-btn-del" @click="$emit('delete-filter', selection.index)">Delete filter</button>
        </div>
      </template>

      <!-- KPI editor -->
      <template v-else-if="selection.type === 'kpi' && kpi">
        <p class="cfg-edit-hint">KPI ({{ selection.index + 1 }})</p>

        <div class="cfg-section">
          <h3 class="cfg-section-title">General</h3>
          <label class="cfg-lbl">Label</label>
          <input class="cfg-inp" v-model="kpiDraft.label" @input="applyKpi" />

          <label class="cfg-lbl">Field</label>
          <select class="cfg-inp" v-model="kpiDraft.field" @change="applyKpi">
            <option value="" disabled>Select field…</option>
            <option v-for="col in columnOptions(kpiDraft.field)" :key="'kpi-' + col" :value="col">{{ col }}</option>
          </select>

          <label class="cfg-lbl">Aggregation</label>
          <select class="cfg-inp" v-model="kpiDraft.agg" @change="applyKpi">
            <option v-for="a in aggOptions" :key="'kagg-' + a" :value="a">{{ capitalize(a) }}</option>
          </select>

          <label class="cfg-lbl">Format</label>
          <select class="cfg-inp" v-model="kpiDraft.format" @change="applyKpi">
            <option value="number">Number</option>
            <option value="currency">Currency</option>
            <option value="percent">Percent</option>
          </select>

          <label class="cfg-lbl">Prefix</label>
          <input class="cfg-inp" v-model="kpiDraft.prefix" @input="applyKpi" />
          <label class="cfg-lbl">Suffix</label>
          <input class="cfg-inp" v-model="kpiDraft.suffix" @input="applyKpi" />
          <label class="cfg-lbl">Decimals</label>
          <input class="cfg-inp" type="number" min="0" max="6" v-model.number="kpiDraft.decimals" @input="applyKpi" />
        </div>

        <div class="cfg-section cfg-section-danger">
          <button type="button" class="cfg-btn-del" @click="$emit('delete-kpi', selection.index)">Delete KPI</button>
        </div>
      </template>

      <!-- Widget editor -->
      <template v-else-if="selection.type === 'widget' && widget">
        <p class="cfg-edit-hint">Widget: {{ widget.id || 'untitled' }}</p>

        <div class="cfg-section">
          <h3 class="cfg-section-title">General</h3>
          <label class="cfg-lbl">Title</label>
          <input class="cfg-inp" v-model="wDraft.title" @input="applyWidget" />

          <label class="cfg-lbl">Chart type</label>
          <select class="cfg-inp" v-model="wDraft.type" @change="applyWidget">
            <option v-for="t in chartTypes" :key="t" :value="t">{{ capitalize(t) }}</option>
          </select>
        </div>

        <div v-if="wDraft.type !== 'table'" class="cfg-section">
          <h3 class="cfg-section-title">Data mapping</h3>
          <label class="cfg-lbl">X axis column</label>
          <select class="cfg-inp" v-model="wDraft.x" @change="applyWidget">
            <option value="" disabled>Select column…</option>
            <option v-for="col in columnOptions(wDraft.x)" :key="'wx-' + col" :value="col">{{ col }}</option>
          </select>

          <label class="cfg-lbl">Y axis column</label>
          <select class="cfg-inp" v-model="wDraft.y" @change="applyWidget">
            <option value="" disabled>Select column…</option>
            <option v-for="col in columnOptions(wDraft.y)" :key="'wy-' + col" :value="col">{{ col }}</option>
          </select>

          <label class="cfg-lbl">Aggregation</label>
          <select class="cfg-inp" v-model="wDraft.agg" @change="applyWidget">
            <option v-for="a in aggOptions" :key="'wagg-' + a" :value="a">{{ capitalize(a) }}</option>
          </select>

          <label class="cfg-lbl">Group by</label>
          <select class="cfg-inp" :value="wDraft.group_by || ''" @change="onGroupByChange($event.target.value)">
            <option value="">(none)</option>
            <option v-for="col in dataColumns" :key="'wg-' + col" :value="col">{{ col }}</option>
          </select>
        </div>

        <div class="cfg-section">
          <h3 class="cfg-section-title">Appearance</h3>

          <template v-if="!isPie">
            <label class="cfg-lbl">Color</label>
            <input class="cfg-inp cfg-color" type="color" :value="wColor" @input="onWColor($event.target.value)" />
          </template>

          <div class="cfg-row-inline">
            <label class="cfg-lbl cfg-lbl-inline">Show data labels</label>
            <input type="checkbox" class="cfg-checkbox" v-model="wDraft.show_data_labels" @change="applyWidget" />
          </div>

          <template v-if="isBar">
            <label class="cfg-lbl">Orientation</label>
            <select class="cfg-inp" v-model="wDraft.orientation" @change="applyWidget">
              <option value="vertical">Vertical</option>
              <option value="horizontal">Horizontal</option>
            </select>

            <label class="cfg-lbl">Stack mode</label>
            <select class="cfg-inp" v-model="wDraft.stack_mode" @change="applyWidget">
              <option value="none">none</option>
              <option value="stacked">stacked</option>
              <option value="stacked_100">stacked_100</option>
            </select>

            <label class="cfg-lbl">Sort bars</label>
            <select class="cfg-inp" v-model="wDraft.sort_bars" @change="applyWidget">
              <option value="none">none</option>
              <option value="asc">asc</option>
              <option value="desc">desc</option>
            </select>

            <label class="cfg-lbl">Top N</label>
            <input
              class="cfg-inp"
              type="number"
              min="0"
              max="50"
              :value="wDraft.top_n == null ? '' : wDraft.top_n"
              @input="onTopN($event.target.value)"
            />
          </template>

          <template v-if="isLine">
            <div class="cfg-row-inline">
              <label class="cfg-lbl cfg-lbl-inline">Smooth line</label>
              <input type="checkbox" class="cfg-checkbox" v-model="wDraft.smooth" @change="applyWidget" />
            </div>
            <div class="cfg-row-inline">
              <label class="cfg-lbl cfg-lbl-inline">Show points</label>
              <input type="checkbox" class="cfg-checkbox" v-model="wDraft.show_points" @change="applyWidget" />
            </div>
            <label class="cfg-lbl">Line width</label>
            <div class="cfg-range-row">
              <input type="range" class="cfg-range" min="1" max="5" v-model.number="wDraft.line_width" @input="applyWidget" />
              <span class="cfg-range-val">{{ wDraft.line_width }}</span>
            </div>
            <label class="cfg-lbl">Line style</label>
            <select class="cfg-inp" v-model="wDraft.line_style" @change="applyWidget">
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
            </select>
          </template>

          <template v-if="isPie">
            <label class="cfg-lbl">Top N slices</label>
            <input
              class="cfg-inp"
              type="number"
              min="0"
              max="20"
              :value="wDraft.top_n == null ? '' : wDraft.top_n"
              @input="onTopN($event.target.value)"
            />
          </template>
        </div>

        <div v-if="wDraft.type === 'table'" class="cfg-section">
          <!-- <h3 class="cfg-section-title">Table columns</h3>
          <label class="cfg-lbl">Row limit</label>
          <input class="cfg-inp" type="number" min="1" max="500" v-model.number="wDraft.row_limit" @input="applyWidget" /> -->

          <label class="cfg-lbl">Visible columns</label>
          <p v-if="!dataColumns.length" class="cfg-edit-note">Load data to pick columns, or columns will be inferred from rows.</p>
          <div v-else class="cfg-col-checks">
            <label v-for="col in dataColumns" :key="'tbl-' + col" class="cfg-col-check">
              <input type="checkbox" :checked="isTableColumnSelected(col)" @change="toggleTableColumn(col, $event.target.checked)" />
              <span>{{ col }}</span>
            </label>
          </div>
        </div>

        <div v-if="showAxesSection" class="cfg-section">
          <h3 class="cfg-section-title">Axes</h3>
          <label class="cfg-lbl">X axis label</label>
          <input class="cfg-inp" v-model="wDraft.x_axis.label" @input="applyWidget" />
          <label class="cfg-lbl">Y axis label</label>
          <input class="cfg-inp" v-model="wDraft.y_axis.label" @input="applyWidget" />
          <div v-if="showValueAxisTickFormat" class="cfg-axis-format">
            <label class="cfg-lbl">{{ valueAxisTickFormatLabel }}</label>
            <select class="cfg-inp" :value="yAxisTickFormat" @change="setYAxisTickFormat($event.target.value)">
              <option value="full">Full number</option>
              <option value="lakhs">Lakhs</option>
              <option value="crs">Crs</option>
              <option value="hcrs">Hcrs</option>
            </select>
          </div>
        </div>

        <div class="cfg-section">
          <h3 class="cfg-section-title">Layout</h3>
          <p class="cfg-edit-note">Grid uses 12 columns.</p>
          <div class="cfg-row4">
            <div><label class="cfg-lbl sm">x</label><input class="cfg-inp" type="number" min="0" v-model.number="layout.x" @input="applyLayout" /></div>
            <div><label class="cfg-lbl sm">y</label><input class="cfg-inp" type="number" min="0" v-model.number="layout.y" @input="applyLayout" /></div>
            <div><label class="cfg-lbl sm">w</label><input class="cfg-inp" type="number" min="1" max="12" v-model.number="layout.w" @input="applyLayout" /></div>
            <div><label class="cfg-lbl sm">h</label><input class="cfg-inp" type="number" min="1" v-model.number="layout.h" @input="applyLayout" /></div>
          </div>
        </div>

        <div class="cfg-section cfg-section-danger">
          <button type="button" class="cfg-btn-del" @click="$emit('delete-widget', selection.index)">Delete widget</button>
        </div>
      </template>
    </div>

    <div class="cfg-edit-ftr">
      <button type="button" class="cfg-btn-add" @click="$emit('add-filter')">+ Add filter</button>
      <button type="button" class="cfg-btn-add" @click="$emit('add-kpi')">+ Add KPI</button>
      <button type="button" class="cfg-btn-add" @click="$emit('add-widget')">+ Add chart</button>
    </div>
  </aside>
</template>

<script>
import _ from 'lodash'
import { widgetHasNumericYAxis, widgetHasNumericXAxis } from './utils/chartConfig'

const WIDGET_SCALAR_KEYS = [
  'type', 'id', 'title', 'x', 'y', 'agg', 'color', 'row_limit', 'group_by',
  'show_data_labels', 'orientation', 'stack_mode', 'sort_bars', 'top_n',
  'smooth', 'show_points', 'line_width', 'line_style',
]

const defaultKpi = () => ({
  label: '', field: 'cost', agg: 'sum', format: 'number', prefix: '', suffix: '', decimals: 0, color: '',
})

const defaultW = () => ({
  type: 'bar',
  id: '',
  title: 'New chart',
  x: 'branch',
  y: 'cost',
  agg: 'sum',
  color: '#3b82f6',
  row_limit: 25,
  group_by: '',
  show_data_labels: false,
  orientation: 'vertical',
  stack_mode: 'none',
  sort_bars: 'none',
  top_n: null,
  smooth: false,
  show_points: true,
  line_width: 2,
  line_style: 'solid',
  columns: [],
  x_axis: { label: '' },
  y_axis: { label: '', tick_format: 'full' },
})

const defaultFilterDraft = () => ({
  column: '', type: 'multi_select', label: '',
})

export default {
  name: 'ConfigDashboardEditSidebar',
  props: {
    dashboardTitle: { type: String, default: 'Dashboard' },
    selection: { type: Object, default: null },
    kpi: { type: Object, default: null },
    widget: { type: Object, default: null },
    filter: { type: Object, default: null },
    dataColumns: { type: Array, default: () => [] },
  },
  data() {
    return {
      titleDraft: 'Dashboard',
      kpiDraft: defaultKpi(),
      wDraft: defaultW(),
      layout: { x: 0, y: 0, w: 6, h: 1 },
      filterDraft: defaultFilterDraft(),
      optionsText: '',
      chartTypes: ['bar', 'line', 'area', 'pie', 'donut', 'scatter', 'radar', 'table'],
      aggOptions: ['sum', 'count', 'mean', 'min', 'max'],
    }
  },
  computed: {
    kpiColor() {
      const c = this.kpiDraft.color
      if (c && /^#/.test(c)) return c
      return '#3b82f6'
    },
    wColor() {
      const c = this.wDraft.color
      if (c && /^#/.test(c)) return c
      return '#3b82f6'
    },
    isPie() {
      return this.wDraft.type === 'pie' || this.wDraft.type === 'donut'
    },
    isBar() {
      return this.wDraft.type === 'bar'
    },
    isLine() {
      return this.wDraft.type === 'line' || this.wDraft.type === 'area'
    },
    showAxesSection() {
      const t = this.wDraft.type
      return !this.isPie && t !== 'table' && t !== 'radar'
    },
    showValueAxisTickFormat() {
      const cfg = this.wDraft || this.widget || {}
      return widgetHasNumericYAxis(cfg) || widgetHasNumericXAxis(cfg)
    },
    valueAxisTickFormatLabel() {
      const cfg = this.wDraft || this.widget || {}
      return widgetHasNumericXAxis(cfg) ? 'X axis tick format' : 'Y axis tick format'
    },
    yAxisTickFormat() {
      const axis = (this.wDraft && this.wDraft.y_axis) || (this.widget && this.widget.y_axis) || {}
      return axis.tick_format || 'full'
    },
  },
  watch: {
    dashboardTitle: {
      immediate: true,
      handler(t) {
        this.titleDraft = t || 'Dashboard'
      },
    },
    kpi: {
      immediate: true,
      deep: true,
      handler(k) {
        if (!k) return
        this.kpiDraft = _.assign(defaultKpi(), _.pick(k, Object.keys(defaultKpi())))
      },
    },
    widget: {
      immediate: true,
      deep: true,
      handler(w) {
        if (!w) return
        const draft = _.assign(defaultW(), _.pick(w, WIDGET_SCALAR_KEYS))
        draft.group_by = w.group_by || ''
        draft.x_axis = { label: '', ...(w.x_axis || {}) }
        draft.y_axis = { tick_format: 'full', label: '', ...(w.y_axis || {}) }
        draft.columns = Array.isArray(w.columns) ? [...w.columns] : []
        this.wDraft = draft
        const l = w.layout || { x: 0, y: 0, w: 6, h: 1 }
        this.layout = { x: l.x, y: l.y, w: l.w, h: l.h }
      },
    },
    filter: {
      immediate: true,
      deep: true,
      handler(f) {
        if (!f) return
        this.filterDraft = _.assign(defaultFilterDraft(), _.pick(f, ['column', 'type', 'label']))
        if (f.type == null) this.filterDraft.type = 'multi_select'
        this.optionsText = Array.isArray(f.options) && f.options.length
          ? f.options.join('\n')
          : ''
      },
    },
  },
  methods: {
    applyTitle() {
      this.$emit('patch-title', this.titleDraft)
    },
    ensureAxisObject(axisKey) {
      if (!this.wDraft[axisKey] || typeof this.wDraft[axisKey] !== 'object') {
        this.$set(this.wDraft, axisKey, { label: '', tick_format: 'full' })
      }
    },
    setYAxisTickFormat(val) {
      this.ensureAxisObject('y_axis')
      this.$set(this.wDraft.y_axis, 'tick_format', val || 'full')
      this.applyWidget()
    },
    capitalize(s) {
      if (!s) return ''
      return s.charAt(0).toUpperCase() + s.slice(1)
    },
    columnOptions(current) {
      const cols = this.dataColumns || []
      if (current && !cols.includes(current)) return [current, ...cols]
      return cols
    },
    buildWidgetPatch() {
      const patch = _.pick(this.wDraft, WIDGET_SCALAR_KEYS)
      patch.x_axis = { ...(this.wDraft.x_axis || {}) }
      patch.y_axis = { ...(this.wDraft.y_axis || {}) }
      patch.columns = Array.isArray(this.wDraft.columns) && this.wDraft.columns.length
        ? [...this.wDraft.columns]
        : []
      const groupBy = (this.wDraft.group_by || '').trim()
      patch.group_by = groupBy || null
      if (patch.top_n === '' || patch.top_n == null || Number.isNaN(patch.top_n)) {
        patch.top_n = null
      } else {
        patch.top_n = parseInt(patch.top_n, 10)
      }
      return patch
    },
    onGroupByChange(value) {
      this.wDraft.group_by = value || ''
      this.applyWidget()
    },
    applyKpi() {
      this.$emit('patch-kpi', { index: this.selection.index, patch: { ...this.kpiDraft } })
    },
    onKpiColor(hex) {
      this.kpiDraft.color = hex
      this.applyKpi()
    },
    applyWidget() {
      this.$emit('patch-widget', { index: this.selection.index, patch: this.buildWidgetPatch() })
    },
    onWColor(hex) {
      this.wDraft.color = hex
      this.applyWidget()
    },
    onTopN(raw) {
      const v = raw === '' || raw == null ? null : parseInt(raw, 10)
      this.wDraft.top_n = Number.isFinite(v) ? v : null
      this.applyWidget()
    },
    applyLayout() {
      this.$emit('patch-widget', {
        index: this.selection.index,
        patch: { layout: { ...this.layout } },
      })
    },
    applyFilter() {
      const parts = (this.optionsText || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean)
      const patch = {
        column: (this.filterDraft.column || '').trim() || 'column',
        type: this.filterDraft.type || 'multi_select',
        label: (this.filterDraft.label || '').trim() || (this.filterDraft.column || 'Filter'),
        options: parts.length ? parts : null,
      }
      this.$emit('patch-filter', { index: this.selection.index, patch })
    },
    isTableColumnSelected(col) {
      const cols = this.wDraft.columns
      if (!Array.isArray(cols) || !cols.length) return true
      return cols.includes(col)
    },
    toggleTableColumn(col, checked) {
      let cols = Array.isArray(this.wDraft.columns) ? [...this.wDraft.columns] : []
      if (!cols.length && this.dataColumns.length) {
        cols = this.dataColumns.filter(c => this.isTableColumnSelected(c))
      }
      if (checked) {
        if (!cols.includes(col)) cols.push(col)
      } else {
        cols = cols.filter(c => c !== col)
      }
      this.wDraft.columns = cols
      this.applyWidget()
    },
  },
}
</script>

<style scoped>
.cfg-edit-sidebar {
  width: 300px; flex-shrink: 0; display: flex; flex-direction: column;
  background: var(--dash-surface);
  border-left: 1px solid var(--dash-border);
  min-height: 0;
  z-index: 40;
  box-shadow: -6px 0 24px -8px var(--dash-shadow);
}
.cfg-edit-hdr {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px; border-bottom: 1px solid var(--dash-border);
  flex-shrink: 0;
}
.cfg-edit-hdr h2 { margin: 0; font-size: 1rem; font-weight: 600; }
.cfg-edit-close {
  background: none; border: none; color: var(--dash-text-muted);
  font-size: 1.1rem; cursor: pointer; padding: 4px 8px; border-radius: 4px;
}
.cfg-edit-close:hover { background: var(--dash-surface2); color: var(--dash-text); }
.cfg-edit-body { flex: 1; overflow-y: auto; padding: 12px 14px; -webkit-overflow-scrolling: touch; }
.cfg-section-dashboard {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--dash-border);
}
.cfg-edit-empty {
  color: var(--dash-text-muted); text-align: center; padding: 24px 8px 32px; font-size: .88rem; line-height: 1.5;
}
.cfg-edit-hint { font-size: .75rem; color: var(--dash-accent); font-weight: 600; margin: 0 0 8px 0; text-transform: uppercase; }
.cfg-section {
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--dash-border);
}
.cfg-section:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
.cfg-section-danger { border-bottom: none; padding-top: 4px; }
.cfg-section-title {
  margin: 0 0 10px 0;
  font-size: .78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--dash-text-muted);
}
.cfg-axis-format { margin-top: 2px; }
.cfg-lbl { display: block; font-size: .72rem; color: var(--dash-text-muted); font-weight: 600; margin: 8px 0 4px 0; }
.cfg-lbl.sm { margin-top: 0; }
.cfg-lbl-inline { margin: 0; flex: 1; }
.cfg-inp {
  width: 100%; padding: 6px 10px; border-radius: 6px;
  border: 1px solid var(--dash-input-border); background: var(--dash-input-bg);
  color: var(--dash-text); font-size: .85rem;
  box-sizing: border-box;
}
.cfg-inp:focus { outline: none; border-color: var(--dash-accent); }
.cfg-color { height: 34px; padding: 2px 4px; cursor: pointer; }
.cfg-edit-note { font-size: .72rem; color: var(--dash-text-muted); margin: 4px 0 0 0; line-height: 1.4; }
.cfg-row4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 6px; }
.cfg-row-inline {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; margin-top: 10px;
}
.cfg-checkbox { width: 16px; height: 16px; accent-color: var(--dash-accent); cursor: pointer; flex-shrink: 0; }
.cfg-range-row { display: flex; align-items: center; gap: 10px; }
.cfg-range { flex: 1; accent-color: var(--dash-accent); }
.cfg-range-val { font-size: .8rem; color: var(--dash-text-muted); min-width: 1.2rem; text-align: right; }
.cfg-col-checks {
  display: flex; flex-direction: column; gap: 6px;
  max-height: 180px; overflow-y: auto;
  margin-top: 6px; padding: 8px;
  border: 1px solid var(--dash-border); border-radius: 8px;
  background: var(--dash-input-bg);
}
.cfg-col-check {
  display: flex; align-items: center; gap: 8px;
  font-size: .82rem; color: var(--dash-text); cursor: pointer;
}
.cfg-col-check input { accent-color: var(--dash-accent); }
.cfg-btn-del {
  width: 100%; margin-top: 4px; padding: 9px; border-radius: 8px; border: none;
  background: color-mix(in srgb, #ef4444 14%, transparent); color: #ef4444;
  font-size: .85rem; font-weight: 600; cursor: pointer;
}
.cfg-btn-del:hover { background: #ef4444; color: #fff; }
.cfg-edit-ftr { padding: 10px 14px; border-top: 1px solid var(--dash-border); flex-shrink: 0; display: flex; flex-direction: column; gap: 8px; }
.cfg-btn-add {
  width: 100%; padding: 10px; border-radius: 8px;
  border: 1px dashed var(--dash-accent); background: transparent; color: var(--dash-accent);
  font-size: .85rem; font-weight: 600; cursor: pointer;
}
.cfg-btn-add:hover { background: color-mix(in srgb, var(--dash-accent) 8%, transparent); border-style: solid; }
</style>
